import { FC } from "react";
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import {
  MusicNoteOutlined as MusicNoteOutlinedIcon,
  OpenInNew as OpenInNewIcon,
} from "@material-ui/icons";
import { compact, first, groupBy, map, sortBy } from "lodash";

import { Button, ListingCard } from "components";
import { TROLLEY_PAYMENT_PORTAL_URL } from "constants/index";
import { BaseContainer } from "containers";
import AddedRoyaltyAccountsChecklist, {
  useAddedAccountsChecklistInfo,
} from "containers/AddedRoyaltyAccountsChecklist";
import { useSellerAssetAdministrations } from "services/assetAdministrations";
import {
  useAssetKYWCredentialSubmissions,
  useAssetKYWRoyaltyAccounts,
} from "services/assets";
import {
  AssetKYWCredentialSubmission,
  AssetKYWRoyaltyAccount,
} from "services/assets/types";
import { useAllSellersListings } from "services/listings";
import { useOtherSourceSubmissions } from "services/otherSourceSubmissions";
import { OtherSourceSubmission } from "services/otherSourceSubmissions/types";
import theme, { Theme } from "theme";

import AddNewAccountCardCard from "./AddNewAccountCard";
import AdministrationCard from "./AdministrationCard";
import CredentialSubmissionCard from "./CredentialSubmissionCard";
import OtherSourceSubmissionCard from "./OtherSourceSubmissionCard";
import RoyaltyAccountActions from "./RoyaltyAccountActions";
import RoyaltyAccountCard from "./RoyaltyAccountCard";

export interface SellerAssetAdministrationItem {
  id: number;
  advance_balance: string;
  currency: string;
  deal_url: string;
  deal_title: string;
  last_update: string;
  previous_hubspot_deal_id: string;
  purchased_term: string;
  royalties_received: string;
  asset_administrations: {
    id: number;
    asset_id: number;
    payment_status: string;
  }[];
  hasPaymentStatusUpdate: boolean;
}

const useStyles = makeStyles<Theme, { isXsDown: boolean }>(() => ({
  pageTitleContainer: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(4),
    "& .MuiSvgIcon-root": {
      fontSize: "3.5rem",
      marginBottom: "14px",
    },
  },
  sectionTitle: {
    fontSize: "1.8rem",
    fontWeight: theme.typography.fontWeightRegular,
    margin: theme.spacing(2, 0),
  },
  checklistContainer: {
    display: "flex",
    flexDirection: "row-reverse",
    gap: `${theme.spacing(2)}px`,
    justifyContent: "space-between",
    margin: "auto",
    padding: theme.spacing(3),
    width: "fit-content",
    maxWidth: "960px",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
    },
  },
  spacingTop: {
    marginTop: theme.spacing(4),
  },
  administrationHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

const SellerDashboard: FC = () => {
  const isXsDown = useMediaQuery(theme.breakpoints.down("xs"));
  const classes = useStyles({ isXsDown });
  const {
    isLoading: isAddedSourcesChecklistInfoLoading,
  } = useAddedAccountsChecklistInfo();

  const otherSourceSubmissionsQueries = useOtherSourceSubmissions();
  const otherSourceSubmissions = otherSourceSubmissionsQueries.data || [];

  const assetKYWRoyaltyAccountsQueries = useAssetKYWRoyaltyAccounts();
  const assetKYWRoyaltyAccounts = assetKYWRoyaltyAccountsQueries.data || [];

  const assetKYWCredentialSubmissionQueries = useAssetKYWCredentialSubmissions();
  const assetKYWCredentialSubmission =
    assetKYWCredentialSubmissionQueries.data || [];

  const sellerAssetAdministrations = useSellerAssetAdministrations().data;
  const listingQueries = useAllSellersListings("?filter{kind}=direct_listing");
  const isListingsQueriesLoading = listingQueries.isLoading;
  const listings = listingQueries?.data || [];

  const isOtherSourceSubmission = (
    object: any,
  ): object is OtherSourceSubmission => {
    return "royalty_payor_name" in object;
  };

  const isAssetKYWRoyaltyAccount = (
    object: any,
  ): object is AssetKYWRoyaltyAccount => {
    return "royalty_account" in object;
  };

  const isRoyaltyAccountsLoading =
    assetKYWRoyaltyAccountsQueries.isLoading ||
    assetKYWCredentialSubmissionQueries.isLoading ||
    otherSourceSubmissionsQueries.isLoading;
  const assets =
    assetKYWRoyaltyAccounts || assetKYWCredentialSubmission
      ? [
          ...assetKYWRoyaltyAccounts,
          ...assetKYWCredentialSubmission,
          ...otherSourceSubmissions,
        ]
      : otherSourceSubmissions;

  const sellerAssetAdministrationItems: SellerAssetAdministrationItem[] = map(
    groupBy(
      map(sellerAssetAdministrations, item => item.asset.resale_info),
      "id",
    ),
    resales => {
      const resaleInfo = first(resales);
      const asset_administrations = map(
        sellerAssetAdministrations.filter(
          item => item.asset.resale_info.id === resaleInfo.id,
        ),
        asset_administration => {
          const { asset, ...assetAdminWithoutAsset } = asset_administration;
          return {
            ...assetAdminWithoutAsset,
            asset_id: asset.id,
          };
        },
      );
      return {
        ...resaleInfo,
        asset_administrations,
        hasPaymentStatusUpdate: !!compact(
          asset_administrations.map(
            assetAdministration => assetAdministration.payment_status,
          ),
        ).length,
      };
    },
  );

  const getAssetSortByKey = (
    asset:
      | AssetKYWCredentialSubmission
      | AssetKYWRoyaltyAccount
      | OtherSourceSubmission,
  ) => {
    if (isOtherSourceSubmission(asset)) {
      return asset.royalty_payor_name.toLowerCase();
    }

    if (isAssetKYWRoyaltyAccount(asset)) {
      const royaltyAccount = asset.royalty_account;
      return `${royaltyAccount.royalty_payor.name.toLowerCase()} ${royaltyAccount.username.toLowerCase()} ${royaltyAccount.subaccount_name.toLowerCase()}`;
    }

    return `${asset.credential_submission.royalty_payor.name.toLowerCase()} ${asset.credential_submission.username.toLowerCase()}`;
  };

  const getAssetGroupByKey = (
    asset:
      | AssetKYWCredentialSubmission
      | AssetKYWRoyaltyAccount
      | OtherSourceSubmission,
  ) => {
    if (isOtherSourceSubmission(asset)) {
      return `other-source-${asset.id}`;
    }

    if (isAssetKYWRoyaltyAccount(asset)) {
      return `royalty-account-${asset.royalty_account.id}`;
    }
    return `credential-submission-${asset.id}`;
  };
  const assetsSorted = sortBy(assets, getAssetSortByKey);
  const assetGroups = groupBy(assetsSorted, getAssetGroupByKey);

  return (
    <BaseContainer>
      <Box className={classes.pageTitleContainer}>
        <Typography variant="h1">
          <MusicNoteOutlinedIcon />
          My Royalties
        </Typography>
        {(isAddedSourcesChecklistInfoLoading ||
          isListingsQueriesLoading ||
          isRoyaltyAccountsLoading) && (
          <CircularProgress style={{ marginLeft: theme.spacing(2) }} />
        )}
      </Box>
      {!isAddedSourcesChecklistInfoLoading && (
        <Card className={classes.checklistContainer}>
          <RoyaltyAccountActions />
          <AddedRoyaltyAccountsChecklist removeBoxShadow />
        </Card>
      )}
      {!!listings.length && (
        <>
          <Typography variant="h2" className={classes.sectionTitle}>
            Listings ({listings.length})
            {isListingsQueriesLoading && (
              <CircularProgress
                size="1.5rem"
                style={{ marginLeft: theme.spacing(2) }}
              />
            )}
          </Typography>
          {map(listings, (listing, index) => (
            <Box key={index} marginTop={index > 0 ? 1 : 0}>
              <ListingCard listing={listing} />
            </Box>
          ))}
        </>
      )}

      {!!sellerAssetAdministrations?.length && (
        <Box mt={4}>
          <Grid container style={{ marginBottom: theme.spacing(5) }}>
            <Grid item xs={12} className={classes.administrationHeader}>
              <Typography variant="h2" className={classes.sectionTitle}>
                Administration {`(${sellerAssetAdministrations?.length})`}
              </Typography>
              <Button
                customColor="green"
                variant="contained"
                component="a"
                href={TROLLEY_PAYMENT_PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {isXsDown ? "Portal" : "Payment Portal"}&nbsp;&nbsp;
                <OpenInNewIcon />
              </Button>
            </Grid>
            <Grid container spacing={2}>
              {sellerAssetAdministrationItems?.map(
                (sellerAssetAdministrationItem, index) => {
                  return (
                    <Grid item xs={12} md={6} key={index}>
                      <AdministrationCard
                        sellerAssetAdministrationItem={
                          sellerAssetAdministrationItem
                        }
                      />
                    </Grid>
                  );
                },
              )}
            </Grid>
          </Grid>
        </Box>
      )}

      {!isRoyaltyAccountsLoading && listingQueries.isFetched && (
        <Grid container style={{ marginBottom: theme.spacing(5) }}>
          <Grid item xs={12}>
            <Typography variant="h2" className={classes.sectionTitle}>
              Royalty Accounts {`(${Object.keys(assetGroups).length})`}
            </Typography>
          </Grid>
          <Grid container spacing={2}>
            {map(assetGroups, (assetGroup, index) => {
              const asset = first(assetGroup);
              if (isOtherSourceSubmission(asset)) {
                return (
                  <Grid item xs={12} md={6} key={index}>
                    <OtherSourceSubmissionCard otherSourceSubmission={asset} />
                  </Grid>
                );
              }

              if (isAssetKYWRoyaltyAccount(asset)) {
                const royaltyAccount = asset.royalty_account;
                return (
                  <Grid item xs={12} md={6} key={index}>
                    <RoyaltyAccountCard
                      assets={assetGroup as AssetKYWRoyaltyAccount[]}
                      royaltyAccount={royaltyAccount}
                      royaltyPayor={royaltyAccount.royalty_payor}
                    />
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} md={6} key={index}>
                  <CredentialSubmissionCard
                    asset={asset as AssetKYWCredentialSubmission}
                  />
                </Grid>
              );
            })}
            <Grid item xs={12} md={6}>
              <AddNewAccountCardCard />
            </Grid>
          </Grid>
        </Grid>
      )}
    </BaseContainer>
  );
};

export default SellerDashboard;
